import React from "react";
import {
  ListItemButton,
  ListItemIcon,
  ListItemText,
  alpha,
  Tooltip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useNavigate, useLocation, Link as RouterLink } from "react-router-dom";

interface SidebarNavItemProps {
  item: { text: string; icon: JSX.Element; path: string; fontSize?: string };
  open: boolean;
  selected?: boolean;
  onClick?: () => void;
}

const SidebarNavItem: React.FC<SidebarNavItemProps> = ({
  item,
  open,
  selected,
  onClick,
}) => {
  const theme = useTheme();
  const location = useLocation();
  const isActive = selected ?? location.pathname === item.path;

  return (
    <Tooltip
      title={!open ? item.text : ""}
      placement="right"
      arrow
      enterDelay={500}
    >
      <ListItemButton
        component={RouterLink}
        to={item.path}
        onClick={onClick}
        selected={isActive}
        sx={{
          borderRadius: 3,
          minHeight: 50,
          mx: 1,
          justifyContent: open ? "flex-start" : "center",
          px: open ? 2.5 : 1.5,
          py: 1.2,
          fontFamily: "'Lexend', sans-serif",
          transition: "all 0.2s ease",
          backgroundColor: isActive
            ? "var(--primary-100)"
            : "transparent",
          color: isActive
            ? "var(--primary-700)"
            : "var(--neutral-1000)",
          border: isActive 
            ? "1px solid var(--primary-300)" 
            : "1px solid transparent",
          "&:hover": {
            backgroundColor: isActive 
              ? "var(--primary-200)"
              : "var(--neutral-100)",
            color: "var(--primary-700)",
            transform: "translateY(-1px)",
            boxShadow: "var(--shadow-sm)",
            "& .MuiListItemIcon-root": {
              color: "var(--primary-700)",
            },
          },
          "&.Mui-selected:hover": {
            backgroundColor: "var(--primary-200)",
          },
        }}
        aria-current={isActive ? "page" : undefined}
      >
        <ListItemIcon
          sx={{
            minWidth: 0,
            mr: open ? 1.5 : 0,
            justifyContent: "center",
            color: isActive
              ? "var(--primary-700)"
              : "var(--neutral-1000)",
            transition: "all 0.2s ease",
            fontSize: "1.1rem",
          }}
        >
          {item.icon}
        </ListItemIcon>

        {open && (
          <ListItemText
            primary={item.text}
            sx={{
              "& .MuiListItemText-primary": {
                fontWeight: isActive ? 600 : 500,
                fontSize: item.fontSize ?? "0.875rem",
                fontFamily: "'Lexend', sans-serif",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                color: "inherit",
              },
            }}
          />
        )}
      </ListItemButton>
    </Tooltip>
  );
};

export default SidebarNavItem;
